import { CONTENT_MODE } from '@make-software/csprclick-core-types';
import { ClickProvider } from '@make-software/csprclick-ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ConnectKitProvider } from 'connectkit';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';

import './index.css';

import { wagmiConfig } from 'config';
import { appRouter } from 'router/appRouter';
import { telemetryEnvironment } from 'utils/telemetryEnvironment';

// CSPR.click configuration
const clickOptions = {
  appName: config.cspr_click_app_name,
  contentMode: CONTENT_MODE.IFRAME,
  providers: ['casper-wallet', 'ledger', 'torus-wallet', 'metamask-snap'],
  appId: config.cspr_click_app_id,
  csprclickHost: config.cspr_click_host,
};

// React Query client for Wagmi
const queryClient = new QueryClient();

// Sentry initialization — DSN is injected via config.js at deploy time
if (config.sentry_dsn) {
  Sentry.init({
    dsn: config.sentry_dsn,
    environment: telemetryEnvironment(window.location.hostname, config.sentry_environment),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0.05,
    sendClientReports: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    beforeSend(event) {
      const mechanism = event.exception?.values?.[0]?.mechanism;
      if (mechanism?.handled === false && !event.tags?.errorBoundary) {
        return null;
      }

      return event;
    },
  });
}

// Deployments must retain old chunks. Never reload on preload failure: wallet signing
// callbacks and submitted transaction state may exist only in this document.

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ConnectKitProvider>
          <ClickProvider options={clickOptions}>
            <RouterProvider router={appRouter} />
          </ClickProvider>
        </ConnectKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  </React.StrictMode>
);
